/**
 * Status consolidado do Dashboard (D8).
 *
 * GUPM-221: o semáforo passa a exibir o NÍVEL ANM DA ESTRUTURA (`useDamScope`),
 * não o nível mais alto de instrumento. TARP de emergência e declaração ANM do RT
 * são fatos da barragem que o pior instrumento não enxerga — uma TARP de
 * emergência na B tem que pintar o semáforo mesmo com todos os instrumentos em
 * NORMAL.
 *
 * O caminho inverso NÃO vale: ocorrência automática (instrumento fora da faixa,
 * TARP aberta) deixa a estrutura em Observação, e Observação **não pinta o
 * semáforo** — `nivelAnmSemaforo` já colapsou para Normal antes de chegar aqui,
 * e o tipo `NivelAnmExibido` impede que `OBSERVACAO` entre. Nível de estrutura só
 * existe declarado pelo RT, ou na emergência. Quem mostra Observação é o chip do
 * header.
 *
 * Os mini-cards abaixo contam INSTRUMENTOS por nível: são duas dimensões
 * distintas, e o card mostra as duas.
 *
 * GUPM-228 — o resumo ganhou o contador "Sem leitura". Sem ele a soma dos
 * mini-cards deixava de fechar com a frota: até este card os instrumentos nunca
 * medidos entravam no verde (a soma fechava mentindo) e, ao saírem, passaram a
 * não aparecer em contagem nenhuma (a soma passou a omitir — 57 de 103 na
 * produção de 04/08/2026, com 46 sem uma única leitura).
 */
import { STATUS_ESTRUTURA_UI } from '@/ui';
import { NIVEL_EXIBIDO_UI } from '@/ui/tokens';
import type { NivelAnmExibido } from '@/domain/estrutura';
import {
  SEM_FAIXA, SEM_LEITURA, SEM_SINAL, FORA_DE_SERVICO, type NivelExibido,
} from '@/domain/instrumentos/nivel-exibido';
import CronTimer from './CronTimer';

interface Props {
  /** Nível ANM consolidado A/B/C — pior entre as barragens visíveis, sem Observação. */
  status: NivelAnmExibido;
  byLevel: Record<NivelExibido, number>;
  /** Timestamp da última leitura ingerida — alimenta o timer do cron (D8). */
  lastUpdateTs: Date | null;
}

/** Cinza `jf-muted` — a cor de "não há o que sinalizar aqui". */
const NEUTRO = '#94a3b8';

/**
 * Ordem dos contadores e o que cada um faz quando vale ZERO — GUPM-325.
 *
 * Duas famílias, na ordem: primeiro a SEVERIDADE (os 4 níveis do TARP na ordem de
 * gravidade, mais a perda de sinal, que o catálogo classifica como ALERTA), depois
 * o INVENTÁRIO (os estados em que a tela não afirma nível nenhum). Ler o começo
 * responde "como está a barragem"; o fim, "de quanta coisa eu não sei".
 *
 * `modo` diz como o card se comporta:
 * - `'acende-com-contagem'` — fica sempre na tela; ganha a cor do nível só quando
 *   há população. Zero aqui é informação ("nenhum instrumento em emergência") e o
 *   card é o lugar onde ela se lê — mas em vermelho ele competia, na mesma tela,
 *   com o alerta que era real.
 * - `'sempre-aceso'` — exceção única, `NORMAL`: zero instrumentos normais é
 *   informação GRAVE, não ausência de informação. Apagar esconderia o pior estado
 *   possível da frota.
 * - `'neutro-ou-oculto'` — inventário. Some do DOM em zero (não informa nada e
 *   ocupava um quarto da grade) e, com população, entra sempre em CINZA: nenhum
 *   matiz de severidade para um estado que não afirma nível. Note que a cor sai
 *   daqui, não de `NIVEL_EXIBIDO_UI` — o token segue intacto, porque mexer nele
 *   mudaria mapa, `InstrumentTable` e `ThresholdsPorEstrutura` junto.
 *
 * **Esta lista é LITERAL, não `Record<NivelExibido, …>`: o `tsc` NÃO acusa a falta
 * de um nível aqui.** Já custou caro duas vezes (GUPM-303, GUPM-307). Estado novo
 * na união ⇒ acrescentar uma entrada, decidindo a qual família ele pertence.
 *
 * A grade é `sm:grid-cols-4` sem `col-span` nenhum. A versão anterior somava spans
 * à mão numa grade de 12 e quebrou quando o oitavo card entrou (5×3 + 3×4 = 27):
 * o layout foi para três linhas com `EMERGENCIA` — o indicador mais importante da
 * tela — sozinho na última. Com número de cards VARIÁVEL essa aritmética é
 * insustentável; 4 colunas fixas resolvem sozinhas, em qualquer combinação.
 */
type ModoDoCard = 'sempre-aceso' | 'acende-com-contagem' | 'neutro-ou-oculto';

const RESUMO: { nivel: NivelExibido; modo: ModoDoCard }[] = [
  { nivel: 'NORMAL', modo: 'sempre-aceso' },
  { nivel: 'ATENCAO', modo: 'acende-com-contagem' },
  { nivel: 'ALERTA', modo: 'acende-com-contagem' },
  { nivel: 'EMERGENCIA', modo: 'acende-com-contagem' },
  { nivel: SEM_SINAL, modo: 'acende-com-contagem' },
  { nivel: SEM_LEITURA, modo: 'neutro-ou-oculto' },
  { nivel: FORA_DE_SERVICO, modo: 'neutro-ou-oculto' },
  { nivel: SEM_FAIXA, modo: 'neutro-ou-oculto' },
];

export default function SemaphoreCard({ status, byLevel, lastUpdateTs }: Props) {
  const ui = STATUS_ESTRUTURA_UI[status];
  const visiveis = RESUMO.filter(
    ({ nivel, modo }) => modo !== 'neutro-ou-oculto' || byLevel[nivel] > 0,
  );
  return (
    <div data-testid="status-consolidado" className="rounded-xl border border-jf-border bg-jf-surface p-4 mb-3">
      <div className="flex flex-wrap items-center gap-5">
        {/* Semáforo + status consolidado + timer do cron (D8), na coluna estreita */}
        <div className="flex flex-col items-center gap-2 w-[140px]">
          {/* 96px (w-24), não 80: a coluna do semáforo media 143px contra 157px do
              grid de mini-cards ao lado, e o `items-center` virava essa diferença em
              vão morto — 8px acima e 8 abaixo do conjunto. Crescer o círculo consome
              exatamente esse vão: o card sai de 191px para 193px. */}
          <div
            className={`flex h-24 w-24 flex-col items-center justify-center rounded-full border-2 ${status !== 'NORMAL' ? 'animate-pulse' : ''}`}
            style={{ borderColor: ui.color, background: ui.bg }}
          >
            {/* Ícone dos 6 estados vem do próprio STATUS_ESTRUTURA_UI (o LevelIcon
                só cobre os 4 níveis de instrumento — não tem Observação/Crítico). */}
            <span className="text-3xl leading-none" aria-hidden>{ui.icon}</span>
            {/* O rótulo mora DENTRO do círculo, então o que o limita é a CORDA na
                altura do texto, não o diâmetro. Medido a 96px: 86px de corda contra
                74px de "Observação", o mais longo dos seis rótulos. A 80px a corda
                era 71px e "Observação"/"Emergência" vazavam pela borda — por isso
                sai o `tracking-wide`, que custava 2px sem ganhar legibilidade. */}
            <span
              data-testid="semaforo-nivel"
              className="mt-1 px-1 text-[10px] font-black uppercase leading-tight"
              style={{ color: ui.color }}
            >
              {ui.label}
            </span>
          </div>
          {/* D8 — "Status consolidado em <ts>" + barra + próxima, na largura da coluna.
              É este timer (e o balde "Sem sinal", permanente logo ao lado) o sentinela
              de ingestão parada — não o contador "Sem leitura", que conta instrumento
              que NUNCA mediu e some quando vale zero. */}
          <CronTimer lastUpdateTs={lastUpdateTs} />
        </div>

        {/* Mini stat-cards por nível — um rótulo para o bloco, em vez de repetir
            "instrumentos" sob cada um dos contadores. */}
        <div className="flex-1 min-w-[180px]">
          <div className="mb-1.5 text-[10px] font-bold uppercase tracking-wide text-jf-muted">
            Resumo de instrumentos
          </div>
          <div data-testid="resumo-grade" className="grid grid-cols-2 gap-2 sm:grid-cols-4">
            {visiveis.map(({ nivel, modo }) => {
              const u = NIVEL_EXIBIDO_UI[nivel];
              const aceso =
                modo === 'sempre-aceso' ||
                (modo === 'acende-com-contagem' && byLevel[nivel] > 0);
              return (
                <div
                  key={nivel}
                  data-testid={`resumo-${nivel}`}
                  className={`rounded-lg p-2 border text-center ${aceso ? '' : 'border-jf-border'}`}
                  style={aceso ? { background: u.bg, borderColor: u.border } : undefined}
                >
                  <div
                    data-testid="resumo-valor"
                    className="text-2xl font-black leading-none"
                    style={{ color: aceso ? u.color : NEUTRO }}
                  >
                    {byLevel[nivel]}
                  </div>
                  <div
                    className="text-[10px] font-bold mt-1"
                    style={{ color: aceso ? u.color : NEUTRO }}
                  >
                    {u.label}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
}
